import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import api from "../../../globals/axios";
import {
  Box,
  Typography,
  Grid,
  Container,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import LoaderContainer from "../../../components/LoaderContainer";
import CustomAppBar from "../../../components/AppBar";
import {
  CustomLabel,
  CustomTitle,
  CustomValue,
} from "../../../components/Custom/CustomTypography";
import GenericButton from "../../../components/Custom/GenericButton";
import axios from "axios";
import { WEB_PAGE_TO_PDF_API_KEY } from "../../../globals/const";
import { useAlert } from "../../../components/Alert";
import { getAuth } from "../../../globals/authentication";

const SatelliteDataReportsListing = () => {
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const [loadingStates, setLoadingStates] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedFarmId, setSelectedFarmId] = useState(null);
  const { token, user } = getAuth() || {};

  useEffect(() => {
    const fetchDates = async () => {
      try {
        setLoading(true);
        const response = await api.get(
          "/farmer/get-farmer-satellite-data-dates/"
        );
        setFarms(response.data.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching satellite data dates:", err);
        setError("Failed to fetch satellite data dates");
      } finally {
        setLoading(false);
      }
    };

    fetchDates();
  }, []);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event, farmId, date) => {
    setAnchorEl(event.currentTarget);
    setSelectedFarmId(farmId);
    setSelectedDate(date);
  };

  const handleMenuItemClick = (language) => {
    setAnchorEl(null);
    generatePdf(selectedFarmId, selectedDate, language);
  };

  const generatePdf = async (farmId, date, language) => {
    const encryptedId = getEncryptedId(date, farmId);
    if (!encryptedId || !language || !token) {
      setLoadingStates((prev) => ({
        ...prev,
        [`${farmId}-${date}`]: false,
      }));
      showAlert("Failed to generate PDF");
      return;
    }
    const url = `${window.location.origin}/farmer/satellite-data-report/${encryptedId}/${language}/${token}`;
    setLoadingStates((prev) => ({
      ...prev,
      [`${farmId}-${date}`]: true,
    }));
    try {
      const response = await axios.post(
        "https://page-pdf.farmapp.in/generate-screenshot",
        { url },
        {
          headers: {
            accept: "application/json",
            "api-key": WEB_PAGE_TO_PDF_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      const taskId = response.data.task_id;
      pollPdfStatus(taskId, farmId, date);
    } catch (err) {
      console.error("Error generating PDF:", err);
      setLoadingStates((prev) => ({
        ...prev,
        [`${farmId}-${date}`]: false,
      }));
    }
  };

  const pollPdfStatus = async (taskId, farmId, date) => {
    const farmer_name = user?.app_user?.full_name;
    const intervalId = setInterval(async () => {
      try {
        const response = await axios.get(
          `https://page-pdf.farmapp.in/get-screenshot?task_id=${taskId}`,
          {
            headers: {
              accept: "application/json",
              "api-key": WEB_PAGE_TO_PDF_API_KEY,
            },
          }
        );
        if (response.data.status === "SUCCESS") {
          const { download_url } = response.data || {};
          handleFileDownload(download_url, farmId, farmer_name, date);
          clearInterval(intervalId);
          setLoadingStates((prev) => ({
            ...prev,
            [`${farmId}-${date}`]: false,
          }));
        }
      } catch (err) {
        console.error("Error polling PDF status:", err);
      }
    }, 5000);
  };

  const handleFileDownload = (downloadUrl, farm_id, farmer_name, date) => {
    if (!downloadUrl) {
      showAlert(t("Failed to download PDF"));
      return;
    }

    const formattedDate = date.replace(/[\s-]/g, "");
    const fileName = `${farmer_name}-${farm_id}-${formattedDate}.pdf`;
    const fileDownloadUrl = `https://page-pdf.farmapp.in${downloadUrl}`;

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "DOWNLOAD_FILE",
          fileUrl: fileDownloadUrl,
          fileName: fileName,
        })
      );
      return;
    }
    downloadFileWithName(fileDownloadUrl, fileName);
  };

  const downloadFileWithName = (url, fileName) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getEncryptedId = (date, farmId) => {
    const formattedDate = date.replace(/-/g, "");
    return `${formattedDate}${farmId}`;
  };

  if (loading) return <LoaderContainer loading={loading} />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <>
      <Helmet>
        <title>Satellite Data Reports - Farmapp</title>
      </Helmet>
      <CustomAppBar />

      <Container maxWidth="xs">
        <CustomTitle>{t("Satellite Data Reports")}</CustomTitle>
        {farms.map((farm) => (
          <Box key={farm.farm_id} sx={{ mb: 4 }}>
            <Box mt={1}>
              <CustomLabel>{farm.farm_name}</CustomLabel>
            </Box>

            <Box mt={2}>
              <Grid container spacing={1}>
                {farm.satellite_data_dates.slice(0, 5).map((date, index) => (
                  <Grid item xs={12} key={index}>
                    <Box sx={{ p: 1, borderRadius: 1 }}>
                      <Grid container spacing={1}>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <CustomValue>
                            {index + 1}. {date}
                          </CustomValue>
                        </Grid>

                        <Grid item xs={6}>
                          <GenericButton
                            aria-controls="simple-menu"
                            aria-haspopup="true"
                            onClick={(event) =>
                              handleClick(event, farm.farm_id, date)
                            }
                            disabled={loadingStates[`${farm.farm_id}-${date}`]}
                            backgroundColor={"#4CAF50"}
                            fontColor={"#fff"}
                            fontSize={"14px"}
                            padding={"6px 10px"}
                          >
                            {loadingStates[`${farm.farm_id}-${date}`] ? (
                              <CircularProgress size={24} />
                            ) : (
                              t("Download PDF")
                            )}
                          </GenericButton>
                          <Menu
                            id="simple-menu"
                            anchorEl={anchorEl}
                            keepMounted
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                          >
                            <MenuItem onClick={() => handleMenuItemClick("en")}>
                              {t("English")}
                            </MenuItem>

                            <MenuItem onClick={() => handleMenuItemClick("mr")}>
                              {t("Marathi (Beta)")}
                            </MenuItem>
                          </Menu>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        ))}
      </Container>
    </>
  );
};

export default SatelliteDataReportsListing;
